'use server';

/**
 * «Нарисовать персонажей» / «Нарисовать страницы» (§15.1): each button is an
 * add-on order with its own estimate. The workspace asks for the estimate
 * (confirm dialog), then places the order: it is journalled, and the
 * illustration run waiting on btn1 / btn2 goes on (pressButton).
 */
import { SessionService } from '@/contexts/authentication';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import type { AddOnPart } from '../order/types';

export async function estimateAddOn(storyId: string, part: AddOnPart): Promise<ActionResult<{ layerId: string; credits: number }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { getOrderPlanService } = await import('../order/server');
    const found = await getOrderPlanService().addOnFor(storyId, familyId, part);
    if (!found) return { success: false, error: 'Not found', code: 'ADDON_NOT_WAITING' };
    return { success: true, data: { layerId: found.layer.id, credits: found.credits } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('estimateAddOn', e) };
  }
}

/**
 * Place the add-on order. code 'ADDON_NO_RUN': the layer has no v2 run yet
 * (an order still made by the v1 executor) — the caller falls back to the v1
 * action for that part.
 */
export async function placeAddOn(storyId: string, part: AddOnPart): Promise<ActionResult<{ credits: number }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { getOrderPlanService } = await import('../order/server');
    const orders = getOrderPlanService();
    const found = await orders.addOnFor(storyId, familyId, part);
    if (!found) return { success: false, error: 'Not found', code: 'ADDON_NOT_WAITING' };
    // an order the v1 executor made (its story is final, the v2 layer never started): the v1 action draws it
    const storyReady = (await orders.viewForStory(storyId, familyId))?.layers.some((l) => l.kind === 'story' && l.state === 'ready');
    if (!found.layer.params.started && storyReady) {
      const { illustrationRunOf } = await import('../illustration/buttons.server');
      if (!(await illustrationRunOf(found.layer.id))) return { success: false, error: 'Not found', code: 'ADDON_NO_RUN' };
    }
    // the executor holds the add-on's own estimate, then the run passes the button
    // (now, or when it reaches it — the text may still be being written)
    const { getOrderExecutor } = await import('../executor/server');
    const executor = getOrderExecutor();
    const pressed = await executor.pressLayerButton(found.layer.id, part, familyId);
    if (!pressed.ok) {
      return pressed.code === 'INSUFFICIENT_ALLOWANCE'
        ? { success: false, error: 'insufficient_allowance', code: 'INSUFFICIENT_ALLOWANCE' }
        : { success: false, error: 'Not found', code: 'ADDON_NOT_WAITING' };
    }
    const layer = await executor.layer(found.layer.id);
    const reservationId = layer ? executor.billing.current(layer)?.id ?? null : null;
    await orders.recordAddOn(found.order.id, found.layer.id, part, pressed.credits, reservationId);
    return { success: true, data: { credits: pressed.credits } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('placeAddOn', e) };
  }
}
